import { useState, useMemo, useEffect, useTransition } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  ColumnFiltersState,
  RowSelectionState,
  PaginationState,
} from "@tanstack/react-table";
import "./GenericTable.scss";
import { TableActionsColumn } from "./TableActionColumn";
import { TableSelectColumn } from "./TableSelectColumn";
import { Filter, fuzzyFilter } from "./TableFilter";
import { TablePagination } from "./TablePagination";
import { GenericTableProps } from "./GenericTableProps";
import { TableSearchInput } from "./TableSearchInput";
import { TableLoader } from "./TableLoader";
import { TableActionMenu } from "./TableActionMenu";
import { ArrowDownIcon, ArrowUpIcon } from "@heroicons/react/24/outline";
import { TableFormatRow } from "./TableFormatRow";
import { useTranslation } from "react-i18next";

export function GenericTable<TData extends object>({
  isLoading,
  title,
  data,
  meta,
  columns,
  initialSorting = [],
  initialFilters = [],
  pageSizeOptions = [10, 20, 30, 40, 50],
  onMore,
  onEdit,
  onDelete,
  pageCount,
  onPaginationChange,
  actionButtons,
  globalFilter = "",
  onGlobalFilterChange,
  onRowSelectionChange,
}: GenericTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [columnFilters, setColumnFilters] =
    useState<ColumnFiltersState>(initialFilters);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSizeOptions[0],
  });

  const {t} = useTranslation()
  const computedColumns = useMemo<ColumnDef<TData>[]>(() => {
    const baseColumns: ColumnDef<TData>[] = [];
    baseColumns.push(TableSelectColumn<TData>());
    baseColumns.push(...columns);
    baseColumns.push(TableActionsColumn<TData>({ onMore, onEdit, onDelete, t }));
    return baseColumns;
  }, [columns, onMore, onEdit, onDelete]);

  const table = useReactTable({
    data,
    columns: computedColumns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      rowSelection,
      pagination,
    },
    pageCount,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: onGlobalFilterChange,
    onRowSelectionChange: setRowSelection,
    manualPagination: true,
    onPaginationChange: (updater) => {
      const newState =
        typeof updater === "function" ? updater(pagination) : updater;
      setPagination(newState);
      onPaginationChange?.(newState.pageIndex, newState.pageSize);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    enableRowSelection: true,
  });

  const selectedRows = useMemo(() => {
    return table.getSelectedRowModel().flatRows.map((row) => row.original);
  }, [table, rowSelection]);

  useEffect(() => {
    onRowSelectionChange?.(selectedRows);
  }, [rowSelection]);

  useEffect(() => {
    onPaginationChange?.(pagination.pageIndex, pagination.pageSize);
  }, [pagination.pageIndex, pagination.pageSize]);

  return (
    <div className="table-container">
      <div className="flex justify-between">
        <div className="flex items-start">
          <TableSearchInput
            containerClassName=""
            value={globalFilter}
            onChange={(val) => onGlobalFilterChange?.(val)}
          />
          <div className="px-4 py-2">{title}</div>
        </div>
        <div className="flex">
          <div className="py-2 px-4">
            {selectedRows.length > 1 && <TableActionMenu />}{" "}
          </div>{" "}
          {actionButtons}
        </div>
      </div>

      <table className="data-table">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} colSpan={header.colSpan}>
                  {header.isPlaceholder ? null : (
                    <div
                      className={
                        header.column.getCanSort()
                          ? "cursor-pointer select-none py-2 flex items-center"
                          : "py-2"
                      }
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {{
                        asc: <ArrowUpIcon className="mx-2 w-4 h-4" />,
                        desc: <ArrowDownIcon className="mx-2 w-4 h-4" />,
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                  )}
                  {header.column.getCanFilter() && (
                    <div>
                      <Filter column={header.column} table={table} />
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                <TableFormatRow row={row} />
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={computedColumns.length} className="no-data">
                {isLoading ? (
                  <TableLoader />
                ) : (
                  <div className="text-center">No data available.</div>
                )}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <TablePagination
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        pageSizeOptions={pageSizeOptions}
        pageCount={pageCount ?? table.getPageCount()}
        canPreviousPage={table.getCanPreviousPage()}
        canNextPage={table.getCanNextPage()}
        setPageIndex={table.setPageIndex}
        previousPage={table.previousPage}
        nextPage={table.nextPage}
        setPageSize={table.setPageSize}
        meta={meta}
      />
    </div>
  );
}
